<template>
  <div class="otp-container">
    <input
      v-for="index in 6"
      :key="index - 1"
      :ref="el => inputRefs[index - 1] = el"
      type="text"
      maxlength="1"
      inputmode="numeric"
      autocomplete="one-time-code"
      class="otp-input"
      :value="digits[index - 1]"
      @input="onInput(index - 1, $event)"
      @keydown="onKeyDown(index - 1, $event)"
      @paste="onPaste($event)"
      @focus="$event.target.select()"
    />
  </div>
</template>

<script>
import { ref, onBeforeUpdate, watch } from 'vue';

/**
 * Component for digit-by-digit OTP/2FA input.
 * Provides premium UX with auto-focus to next field and backspace navigation.
 */
export default {
  name: 'OInputDigit',
  props: {
    modelValue: {
      type: String,
      default: ''
    }
  },
  setup(props, { emit }) {
    const digits = ref(Array(6).fill(''));
    const inputRefs = ref([]);

    // Reset refs before update
    onBeforeUpdate(() => {
      inputRefs.value = [];
    });

    // Synchronize external value changes
    watch(() => props.modelValue, (newVal) => {
      if (!newVal) {
        digits.value = Array(6).fill('');
      } else if (newVal.length === 6 && newVal !== digits.value.join('')) {
        digits.value = newVal.split('');
      }
    });

    /**
     * Handles digit input.
     * @param {number} index
     * @param {Event} event
     */
    const onInput = (index, event) => {
      const val = event.target.value;
      
      // Handle the case where the browser might put multiple chars
      const lastChar = val.slice(-1);
      
      if (lastChar && /^\d$/.test(lastChar)) {
        digits.value[index] = lastChar;
        emit('update:modelValue', digits.value.join(''));
        
        // Auto-focus next field
        if (index < 5) {
          inputRefs.value[index + 1]?.focus();
        }
      } else {
        // Reset if invalid input
        digits.value[index] = '';
        event.target.value = '';
      }
    };

    /**
     * Handles special keys like Backspace.
     * @param {number} index
     * @param {KeyboardEvent} event
     */
    const onKeyDown = (index, event) => {
      if (event.key === 'Backspace') {
        if (!digits.value[index] && index > 0) {
          // If current is empty, go back and clear previous
          digits.value[index - 1] = '';
          inputRefs.value[index - 1]?.focus();
          emit('update:modelValue', digits.value.join(''));
        } else {
          // Just clear current
          digits.value[index] = '';
          emit('update:modelValue', digits.value.join(''));
        }
      }
    };

    /**
     * Handles pasting of the whole OTP code.
     * @param {ClipboardEvent} event
     */
    const onPaste = (event) => {
      event.preventDefault();
      const pasteData = event.clipboardData.getData('text').slice(0, 6);
      
      if (!/^\d+$/.test(pasteData)) return;

      const chars = pasteData.split('');
      chars.forEach((char, i) => {
        if (i < 6) digits.value[i] = char;
      });

      emit('update:modelValue', digits.value.join(''));
      
      // Focus the appropriate field after paste
      const nextFocus = Math.min(chars.length, 5);
      inputRefs.value[nextFocus]?.focus();
    };

    return {
      digits,
      inputRefs,
      onInput,
      onKeyDown,
      onPaste
    };
  }
};
</script>

<style scoped>
.otp-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.otp-input {
  width: 100%;
  max-width: 50px;
  min-width: 0;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background-color: #fff;
  color: #111827;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.otp-input:focus {
  border-color: var(--primary-color, #fc6666);
  box-shadow: 0 0 0 4px rgba(var(--primary-rgb, 252, 102, 102), 0.15);
  outline: none;
  transform: translateY(-2px);
}

@media screen and (max-width: 768px) {
  .otp-input {
    max-width: 42px;
    height: 52px;
    font-size: 20px;
    border-radius: 8px;
  }
  .otp-container {
    gap: 8px;
  }
}
</style>